import { Avatar, Chip, SxProps } from "@mui/material";
import { CallMade, Help, Hub, Storage } from "@mui/icons-material";
import OrkesIcon from "../../images/branding/orkes-favicon-purple.png";
import {
  CustomerEnvironment,
  ClusterType,
  isWorkerHostingEnvironment,
} from "utils/saastypes";
import StarterClusterIcon from "images/svg/chip/starter-cloud-cluster.svg";
import StandardClusterIcon from "images/svg/chip/standard-cloud-cluster.svg";
import EnterpriseClusterIcon from "images/svg/chip/enterprise-cloud-cluster.svg";

// Exported so sibling header chips (see MultiRegionChip) share one definition of the chip
// shape rather than drifting from a copy.
export const hostingChipSx = {
  opacity: 1,
  paddingLeft: 1,
  "& span": {
    fontFamily: "Lexend, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    paddingLeft: 2.5,
    paddingRight: 2.5,
    paddingTop: 0.5,
  },
};

const OrkesHostedChip = ({
  sx,
  clusterType,
  icon,
}: {
  clusterType?: String;
  sx: SxProps;
  icon?: string;
}) => (
  <Chip
    label={`${clusterType} Cluster`}
    avatar={
      <Avatar
        sx={{ margin: 0, padding: 0 }}
        alt="O"
        src={icon ? icon : OrkesIcon}
      />
    }
    color="info"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

const CustomerHostedChip = ({ sx }: { sx: SxProps }) => (
  <Chip
    label="Customer Hosted"
    icon={<Storage />}
    color="info"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

// Says what the entity IS (CLOUD-1193). It is the peer of the compute-unit tag rather than
// a trailing attribute, so callers render it as the LEADING chip in the row — see
// ClusterDetailDisplay (listing card) and ClusterDetailsLegacy (detail header). Because it
// occupies that leading slot, HostingChip below renders nothing for a worker environment.
export const HostedWorkersComputeChip = ({ sx = {} }: { sx?: SxProps }) => (
  <Chip
    label="Hosted Workers Compute"
    icon={<Hub />}
    color="info"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

// Marks a primary cluster whose workers run in an attached worker environment
// (CLOUD-1193). Stays in the trailing position. Deliberately NOT a word-order variant of
// the chip above and deliberately not the same glyph: the two mean opposite things ("this
// IS hosted-workers compute" vs. "this cluster's workers live elsewhere") and previously
// rendered identically. The outbound arrow carries "delegated away"; colour alone would
// not distinguish them for anyone with a colour vision deficiency, so icon and label
// differ too. Chrome stays color="info"/outlined like the rest of the row on purpose: this
// theme sets palette.secondary.main to white, so a filled secondary chip renders as an
// invisible pill on the white card.
export const RemoteDelegatedWorkersChip = ({ sx = {} }: { sx?: SxProps }) => (
  <Chip
    label="Remote Delegated Workers"
    icon={<CallMade />}
    color="info"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

const UnknownChip = ({ sx }: { sx: SxProps }) => (
  <Chip
    label="Unknown Cluster Type"
    icon={<Help />}
    color="warning"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

const ChipWithoutIcon = ({
  sx,
  clusterType,
}: {
  sx: SxProps;
  clusterType: String;
}) => (
  <Chip
    label={clusterType}
    color="info"
    variant="outlined"
    sx={{ ...hostingChipSx, ...sx }}
  />
);

export const HostingChip = ({
  cluster,
  sx = {},
}: {
  cluster: CustomerEnvironment;
  sx?: SxProps;
}) => {
  // Worker hosting is a distinct product, not a cluster type, so it is resolved before the
  // ClusterType switch — otherwise a worker environment falls through to UnknownChip. It
  // renders nothing here because HostedWorkersComputeChip already leads the chip row.
  if (isWorkerHostingEnvironment(cluster)) {
    return null;
  }
  switch (cluster?.type) {
    case ClusterType.STARTER:
      return (
        <OrkesHostedChip
          sx={sx}
          clusterType={"Starter"}
          icon={StarterClusterIcon}
        />
      );
    case ClusterType.STANDARD:
      return (
        <OrkesHostedChip
          sx={sx}
          clusterType={"Standard"}
          icon={StandardClusterIcon}
        />
      );
    case ClusterType.TRIAL:
      return <ChipWithoutIcon sx={sx} clusterType={"Enterprise Cluster"} />;
    case ClusterType.ENTERPRISE:
      if (cluster.orkesAccount) {
        return (
          <OrkesHostedChip
            sx={sx}
            clusterType={"Enterprise"}
            icon={EnterpriseClusterIcon}
          />
        );
      } else {
        return <CustomerHostedChip sx={sx} />;
      }
    default:
      return cluster.orkesAccount ? (
        <OrkesHostedChip sx={sx} />
      ) : (
        <UnknownChip sx={sx} />
      );
  }
};
